import {EventsStoredObjects, EventStoredObject, SuggestionObject} from "../../@types/response";

export const MIN_SEARCH_LENGTH = 3;

export type ActiveEventIds = Set<number>;

/**
 * Client-side event search over the local fixtures anagrafica.
 *
 * If `activeIds` is provided, results are restricted to that set (events
 * with active odds for a given section). Pass `null` to search across all
 * fixtures (dropping odds use case).
 *
 * Match rules (all case-insensitive):
 *  - if the query parses as an integer, match by event id
 *  - otherwise substring match on name, category, tournament
 *
 * Sort: name ascending, then date ascending.
 */
export function searchActiveFixtures(
    fixtures: EventsStoredObjects,
    activeIds: ActiveEventIds | null,
    query: string,
): SuggestionObject[] {
    if (query.length < MIN_SEARCH_LENGTH) return [];

    const trimmed = query.trim();
    const asInt = /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : null;
    const needle = trimmed.toLowerCase();

    const matches: EventStoredObject[] = [];
    const iterIds: string[] = activeIds
        ? Array.from(activeIds).map((id) => id.toString())
        : Object.keys(fixtures);
    for (const id of iterIds) {
        const fx = fixtures[id];
        if (!fx) continue;
        if (asInt !== null) {
            if (fx._id === asInt) matches.push(fx);
            continue;
        }
        if (
            fx.name?.toLowerCase().includes(needle) ||
            fx.category?.toLowerCase().includes(needle) ||
            fx.tournament?.toLowerCase().includes(needle)
        ) {
            matches.push(fx);
        }
    }

    matches.sort((a, b) => {
        const byName = a.name.localeCompare(b.name);
        if (byName !== 0) return byName;
        return (a.date ?? 0) - (b.date ?? 0);
    });

    return matches.map((fx) => ({id: fx._id, name: fx.name, sportId: fx.sportId}));
}
